/**
 * NR01 Evolution - Evolução Histórica dos Diagnósticos por CNPJ
 * Mostra a comparação de resultados ao longo do tempo
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { nr01API } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, 
  Loader2, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Calendar,
  BarChart3,
  Building2,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const NIVEL_RISCO_COLORS = {
  baixo: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  medio: 'bg-amber-100 text-amber-700 border-amber-300',
  alto: 'bg-red-100 text-red-700 border-red-300'
};

const NIVEL_RISCO_LABELS = {
  baixo: 'Baixo',
  medio: 'Médio',
  alto: 'Alto'
};

const TendenciaIcon = ({ tendencia }) => {
  if (tendencia === 'melhorou') return <TrendingUp className="w-4 h-4 text-emerald-600" />;
  if (tendencia === 'piorou') return <TrendingDown className="w-4 h-4 text-red-600" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
};

export default function NR01Evolution() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedSetor, setSelectedSetor] = useState(null);

  useEffect(() => {
    fetchEvolution();
  }, []);

  const fetchEvolution = async () => {
    try {
      const response = await nr01API.getEvolution();
      setData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar evolução');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-600" />
      </div>
    );
  }

  if (!data || data.total_diagnosticos === 0) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/company/nr01')} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          
          <Card className="text-center py-12">
            <CardContent>
              <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-700 mb-2">
                Nenhum diagnóstico finalizado
              </h2>
              <p className="text-slate-500 mb-6">
                Para ver a evolução, você precisa ter pelo menos um diagnóstico NR01 finalizado.
              </p>
              <Button onClick={() => navigate('/company/nr01/new')} className="bg-rose-600 hover:bg-rose-500">
                Criar Primeiro Diagnóstico
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const setores = Object.keys(data.evolucao_por_setor || {});

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/company/nr01')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-rose-600" />
                Evolução NR01
              </h1>
              <p className="text-slate-500">Histórico de diagnósticos por CNPJ</p>
            </div>
          </div>
        </div>

        {/* Empresa Info */}
        <Card className="mb-6 border-rose-200 bg-rose-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">{data.empresa}</h3>
                {data.cnpj && (
                  <p className="text-sm text-slate-500">CNPJ: {data.cnpj}</p>
                )}
              </div>
              <div className="ml-auto text-right">
                <p className="text-2xl font-bold text-rose-600">{data.total_diagnosticos}</p>
                <p className="text-sm text-slate-500">diagnósticos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tendências Gerais */}
        {data.tendencias && Object.keys(data.tendencias).length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Tendências (Último vs Anterior)
              </CardTitle>
              <CardDescription>
                Comparação entre o último diagnóstico e o anterior
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {data.dimensoes_info?.map((dim) => {
                  const tendencia = data.tendencias[dim.id];
                  if (!tendencia) return null;
                  
                  return (
                    <div key={dim.id} className="p-4 bg-slate-50 rounded-xl border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-slate-500 font-medium">{dim.nome}</span>
                        <TendenciaIcon tendencia={tendencia.tendencia} />
                      </div>
                      <p className="text-lg font-bold text-slate-900">{tendencia.ultimo}</p>
                      <p className={`text-sm ${tendencia.tendencia === 'melhorou' ? 'text-emerald-600' : tendencia.tendencia === 'piorou' ? 'text-red-600' : 'text-slate-500'}`}>
                        {tendencia.variacao > 0 ? '+' : ''}{tendencia.variacao}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Timeline de Diagnósticos */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-rose-600" />
              Histórico de Diagnósticos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.evolucao.map((diag, index) => (
                <div 
                  key={diag.id} 
                  className="p-4 bg-white border rounded-xl hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/company/nr01/${diag.id}/results`)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center text-rose-600 font-bold text-sm">
                        {data.evolucao.length - index}
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{diag.titulo}</h4>
                        <p className="text-sm text-slate-500">{diag.data}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">{diag.total_respostas} respostas</p>
                      <p className="text-xs text-slate-400">{diag.setores.length} setores</p>
                    </div>
                  </div>
                  
                  {/* Mini gráfico de dimensões */}
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(diag.dimensoes || {}).map(([dimId, dimData]) => (
                      <Badge 
                        key={dimId} 
                        variant="outline"
                        className={NIVEL_RISCO_COLORS[dimData.nivel_risco]}
                      >
                        {data.dimensoes_info?.find(d => d.id === dimId)?.nome?.slice(0, 3) || dimId}: {dimData.media}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Evolução por Setor */}
        {setores.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Evolução por Setor</CardTitle>
              <CardDescription>
                Selecione um setor para ver a evolução detalhada
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Seletor de setores */}
              <div className="flex flex-wrap gap-2 mb-6">
                {setores.map((setor) => (
                  <Button
                    key={setor}
                    variant={selectedSetor === setor ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedSetor(selectedSetor === setor ? null : setor)}
                    className={selectedSetor === setor ? 'bg-rose-600 hover:bg-rose-500' : ''}
                  >
                    {setor}
                  </Button>
                ))}
              </div>

              {/* Dados do setor selecionado */}
              {selectedSetor && data.evolucao_por_setor[selectedSetor] && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-700">
                    Evolução: {selectedSetor}
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Data</th>
                          <th className="text-left p-2">Diagnóstico</th>
                          <th className="text-center p-2">Respostas</th>
                          {data.dimensoes_info?.map((dim) => (
                            <th key={dim.id} className="text-center p-2" title={dim.nome}>
                              {dim.nome.slice(0, 3)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.evolucao_por_setor[selectedSetor].map((item, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                            <td className="p-2 text-slate-600">{item.data}</td>
                            <td className="p-2 font-medium">{item.titulo}</td>
                            <td className="p-2 text-center">{item.respostas}</td>
                            {data.dimensoes_info?.map((dim) => (
                              <td key={dim.id} className="p-2 text-center">
                                {item.dimensoes[dim.id] !== undefined ? (
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    item.dimensoes[dim.id] >= 3.5 ? 'bg-emerald-100 text-emerald-700' :
                                    item.dimensoes[dim.id] >= 2.5 ? 'bg-amber-100 text-amber-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {item.dimensoes[dim.id]}
                                  </span>
                                ) : '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!selectedSetor && (
                <p className="text-center text-slate-500 py-8">
                  Selecione um setor acima para ver a evolução detalhada
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
